// Filters-catalog-driven search: an isolated Vuex module.
import { get as _get, post as _post } from '../../../utils/fetcher'
import { flattenResultRow } from '../../../utils/searchResultsFieldFormat'
import { assetClassStorageKey } from '../../../utils/assetClassState'

const INIT_PAGINATOR = () => ({
  pageIndex: 1,
  pageSize: 100,
  totalItems: 0,
})

const INIT_SEARCH_RESULT = () => ({
  sortBy: '-referenceDate',
  results: [],
})

const INIT_SEARCH_IDS_RESULT = () => ({
  count: {},
  revisionIds: [],
})

// Asset-class-scoped (switching asset class triggers a full reload — see assetClassStorageKey —
// so re-evaluating this at module load, after the reload, picks up the new slug correctly).
const FILTERS_STORAGE_KEY = assetClassStorageKey('searchFilters')

function loadPersistedFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    // Defensive: strip SmartFilter from anything saved by an older build, before persistFilters
    // below stopped writing it out.
    return Array.isArray(parsed) ? parsed.filter((f) => f.key !== 'SmartFilter') : []
  } catch {
    return []
  }
}

// SmartFilter (free text) must never persist across tabs/reloads — the legacy page's own
// free-text search box is never restored from a fresh page load either (only the sidebar's
// dropdown/range filters are). Excluded here rather than at the call sites so every persist path
// (setPendingFilterValue, clearFilters) is covered uniformly.
function persistFilters(filters) {
  localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters.filter((f) => f.key !== 'SmartFilter')))
}

function withFilterValue(entries, key, value) {
  const others = entries.filter((f) => f.key !== key)
  // null/undefined/empty-array value means "not applied" (Pilastro 2) — drop the entry entirely
  // rather than sending a null/empty value, keeping the wire payload minimal. A plain range object
  // with BOTH sides null (e.g. RangeFilter's slider dragged back to its full min/max extremes) is
  // equally "not applied" — without this, the filter stayed in pendingFilters with {from:null,
  // to:null}, incrementing the filter count and showing a stray ActiveFilters chip for a range that
  // isn't actually restricting anything. Composite objects with at least one real value (Perimeter,
  // CompanyFilter) are unaffected since they always carry a non-null discriminant (mode/products_in_test).
  const isEmptyRange =
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value).every((v) => v === null || v === undefined)
  const isEmpty = value === null || value === undefined || (Array.isArray(value) && value.length === 0) || isEmptyRange
  return isEmpty ? others : [...others, { key, value }]
}

export default {
  namespaced: true,

  state: {
    catalog: { groups: [], ungrouped: [] },
    catalogLoading: false,
    issuersByCode: {},

    // Matches the legacy Search.vue behaviour: filter widgets edit a DRAFT (`pendingFilters`) that
    // only takes effect on "Applica filtri" — SmartFilter and chip removal are the two exceptions
    // that search immediately, exactly like the legacy page. `appliedFilters` is what the last
    // search call actually used.
    // Restored from localStorage (Pilastro 2 parity fix): a new tab must reopen with the last
    // selected filters, matching the legacy Search.vue's SidebarFilters persistence behaviour.
    pendingFilters: loadPersistedFilters(),
    appliedFilters: loadPersistedFilters(),

    searchResult: INIT_SEARCH_RESULT(),
    paginator: INIT_PAGINATOR(),
    isSearching: false,
    isLoadingMore: false,
    isLoadingProductIds: false,

    // Columns resolved by the BE from `configs_result_fields` (already SOP/MOP-
    // variant-resolved, see FILTER_CATALOG_DESIGN.md): `{fieldKey, fieldType, tabKey, tabOrder,
    // fieldOrder, sortable, valueSchema}[]`. The FE does not need to know about SOP/MOP itself,
    // it just renders whatever columns come back.
    columns: [],

    // Page chrome state, kept in this module instead of component data() so the
    // "Statistiche"/"Documenti" views can share it across the duplicated chart/pigro logic.
    sidebarOpen: localStorage.getItem(assetClassStorageKey('searchSidebarOpen')) !== 'false',
    currentView: localStorage.getItem(assetClassStorageKey('searchCurrentView')) || 'tab',
    selectedTableTab: 0,

    searchIdsResult: INIT_SEARCH_IDS_RESULT(),
  },

  mutations: {
    setCatalog(state, catalog) {
      state.catalog = catalog
    },
    setCatalogLoading(state, value) {
      state.catalogLoading = value
    },
    setIssuersByCode(state, issuersByCode) {
      state.issuersByCode = issuersByCode
    },
    setPendingFilterValue(state, { key, value }) {
      state.pendingFilters = withFilterValue(state.pendingFilters, key, value)
      persistFilters(state.pendingFilters)
    },
    setAppliedFilterValue(state, { key, value }) {
      state.appliedFilters = withFilterValue(state.appliedFilters, key, value)
    },
    syncAppliedFromPending(state) {
      state.appliedFilters = state.pendingFilters
    },
    clearFilters(state) {
      state.pendingFilters = []
      state.appliedFilters = []
      persistFilters(state.pendingFilters)
    },
    setSearchResult(state, { results, columns }) {
      state.searchResult.results = results
      state.columns = columns
    },
    appendSearchResults(state, results) {
      state.searchResult.results = [...state.searchResult.results, ...results]
    },
    setSortBy(state, sortBy) {
      state.searchResult.sortBy = sortBy
    },
    setPaginator(state, payload) {
      state.paginator = { ...state.paginator, ...payload }
    },
    setSearching(state, value) {
      state.isSearching = value
    },
    setLoadingMore(state, value) {
      state.isLoadingMore = value
    },
    // Gates the fke-widget's render in Search.vue: it must never mount (nor be patched) while the
    // revision ids it filters on are still in flight — see loadProductIds below.
    setLoadingProductIds(state, value) {
      state.isLoadingProductIds = value
    },
    // Matches the legacy Search.vue: totalItems is only ever overwritten once a response actually
    // arrives (resp.paging.totalItems) — resetting it here to 0 made the "X Prodotti filtrati"
    // count (and label) flash to zero on every filter change, before the new search resolves.
    resetPaging(state) {
      state.paginator.pageIndex = 1
    },
    setSidebarOpen(state, value) {
      state.sidebarOpen = value
      localStorage.setItem(assetClassStorageKey('searchSidebarOpen'), value)
    },
    setCurrentView(state, value) {
      state.currentView = value
      localStorage.setItem(assetClassStorageKey('searchCurrentView'), value)
    },
    setSelectedTableTab(state, value) {
      state.selectedTableTab = value
    },
    setSearchIdsResult(state, result) {
      state.searchIdsResult = result || INIT_SEARCH_IDS_RESULT()
    },
  },

  actions: {
    loadFiltersCatalog({ commit }) {
      commit('setCatalogLoading', true)
      return _get('/api/filters-catalog')
        .then((catalog) => commit('setCatalog', catalog))
        .finally(() => commit('setCatalogLoading', false))
    },

    // Reuses the legacy `/api/searchfilters/values` endpoint purely for its issuer code→name map —
    // the catalog's dynamic IssuerCodes options are codes only (see FilterValuesResolver on the BE);
    // this gives the FE the same names the legacy page shows instead of raw codes.
    loadIssuers({ commit }) {
      return _get('/api/searchfilters/values').then((resp) => {
        if (resp != null && resp.issuers != null) {
          const byCode = {}
          resp.issuers.forEach((i) => {
            byCode[i.code] = i.name
          })
          commit('setIssuersByCode', byCode)
        }
      })
    },

    // Draft-only update: filter widgets do NOT search immediately — only
    // "Applica filtri" does. SmartFilter and chip removal call
    // `applyFilterValueImmediately`/`removeFilter` instead.
    setPendingFilterValue({ commit }, payload) {
      commit('setPendingFilterValue', payload)
    },

    // SmartFilter's own live-search behaviour (legacy `_searchText` setter searches on every change).
    applyFilterValueImmediately({ commit, dispatch }, payload) {
      commit('setPendingFilterValue', payload)
      commit('setAppliedFilterValue', payload)
      commit('resetPaging')
      return dispatch('applyFilters')
    },

    removeFilter({ commit, dispatch }, key) {
      commit('setPendingFilterValue', { key, value: null })
      commit('setAppliedFilterValue', { key, value: null })
      commit('resetPaging')
      return dispatch('applyFilters')
    },

    // Removes a single value from a multi-select filter's array (one badge = one value, per the
    // legacy SelectedFilters.vue behaviour) instead of clearing the whole filter entry.
    removeFilterValueItem({ commit, dispatch, state }, { key, item }) {
      const entry = state.pendingFilters.find((f) => f.key === key)
      if (!entry || !Array.isArray(entry.value)) {
        return dispatch('removeFilter', key)
      }
      const newValue = entry.value.filter((v) => JSON.stringify(v) !== JSON.stringify(item))
      commit('setPendingFilterValue', { key, value: newValue })
      commit('setAppliedFilterValue', { key, value: newValue })
      commit('resetPaging')
      return dispatch('applyFilters')
    },

    // Clears just one side ("from" or "to") of a {from,to} range value — used for date ranges
    // (IssueDate/MaturityDate) shown as two independent ActiveFilters chips ("Da"/"A"), where
    // removing one chip must not wipe the other side too.
    removeFilterRangeSide({ commit, dispatch, state }, { key, side }) {
      const entry = state.pendingFilters.find((f) => f.key === key)
      if (!entry || entry.value === null || typeof entry.value !== 'object' || Array.isArray(entry.value)) {
        return dispatch('removeFilter', key)
      }
      const newValue = { ...entry.value, [side]: null }
      const isEmpty = Object.values(newValue).every((v) => v === null || v === undefined)
      commit('setPendingFilterValue', { key, value: isEmpty ? null : newValue })
      commit('setAppliedFilterValue', { key, value: isEmpty ? null : newValue })
      commit('resetPaging')
      return dispatch('applyFilters')
    },

    applyPendingFilters({ commit, dispatch }) {
      commit('syncAppliedFromPending')
      commit('resetPaging')
      return dispatch('applyFilters')
    },

    clearFilters({ commit, dispatch }) {
      commit('clearFilters')
      commit('resetPaging')
      return dispatch('applyFilters')
    },

    // 'search' and 'loadProductIds' run in PARALLEL, not chained. Chaining them left a window in
    // which the results search had already resolved (`isSearching` false, so the fke-widget's
    // enclosing card renders) while `searchIdsResult` was still the previous/empty one: the widget
    // mounted with stale `product-ids` and immediately fetched the FKE corpus, then fetched it
    // again when the real ids landed. `loadProductIds` is dispatched synchronously here, so
    // `isLoadingProductIds` is already true before Vue's first re-render.
    applyFilters({ dispatch, state }) {
      const tasks = [dispatch('search')]
      if (state.currentView === 'pigro') {
        tasks.push(dispatch('loadProductIds'))
      }
      return Promise.all(tasks)
    },

    // Catalog-driven results (columns + rows in one response, see
    // `SearchResultsEndpoint`/FILTER_CATALOG_DESIGN.md). Rows come back key-value shaped
    // (`{id, fields:[{key,value}]}`) and are flattened here into the plain objects `b-table`/
    // `ResultsTable.vue` expect.
    search({ state, commit }) {
      commit('setSearching', true)
      return _post('/api/search/results', {
        filters: state.appliedFilters,
        sort: state.searchResult.sortBy,
        page: state.paginator.pageIndex,
        size: state.paginator.pageSize,
      })
        .then((resp) => {
          if (resp && resp.rows != null) {
            commit('setSearchResult', { results: resp.rows.map(flattenResultRow), columns: resp.columns || [] })
            if (resp.paging) {
              commit('setPaginator', { totalItems: resp.paging.totalItems })
            }
          }
        })
        .finally(() => commit('setSearching', false))
    },

    loadMore({ state, commit }) {
      if (state.isLoadingMore) {
        return Promise.resolve()
      }
      const nextPage = state.paginator.pageIndex + 1
      commit('setLoadingMore', true)
      return _post('/api/search/results', {
        filters: state.appliedFilters,
        sort: state.searchResult.sortBy,
        page: nextPage,
        size: state.paginator.pageSize,
      })
        .then((resp) => {
          if (resp && resp.rows != null) {
            commit('appendSearchResults', resp.rows.map(flattenResultRow))
            commit('setPaginator', { pageIndex: nextPage })
            if (resp.paging) {
              commit('setPaginator', { totalItems: resp.paging.totalItems })
            }
          }
        })
        .finally(() => commit('setLoadingMore', false))
    },

    // Asset-class-scoped (SearchProductIdsEndpoint) — posts the FilterCollection directly, so
    // IC-only filter_keys are never dropped for the FKE "Documenti" widget.
    loadProductIds({ state, commit }) {
      commit('setLoadingProductIds', true)
      return _post('/api/search/productsIds', { filters: state.appliedFilters })
        .then((resp) => {
          if (resp != null && resp.revisionIds != null) {
            commit('setSearchIdsResult', resp)
          }
        })
        .finally(() => commit('setLoadingProductIds', false))
    },

    setCurrentView({ commit, dispatch }, view) {
      commit('setCurrentView', view)
      return dispatch('applyFilters')
    },

    changePageIndex({ commit, dispatch }, pageIndex) {
      commit('setPaginator', { pageIndex })
      return dispatch('search')
    },

    // Does NOT dispatch('search') itself — the 'searchResult.sortBy' watcher (Search.vue) already
    // reacts to the setSortBy mutation below and triggers the search; dispatching here too caused
    // every sort click to fire two POST /api/search/results requests.
    changeSort({ commit }, sortBy) {
      commit('setSortBy', sortBy)
    },

    // Called from Search.vue's created() hook on every mount (including a brand-new tab) — must
    // NOT clear pendingFilters/appliedFilters, or the localStorage-persisted filter selection
    // (restored at module load, see loadPersistedFilters()) gets wiped before it's ever applied.
    resetState({ commit }) {
      commit('setCatalog', { groups: [], ungrouped: [] })
      commit('setSearchResult', { results: [], columns: [] })
      commit('setPaginator', INIT_PAGINATOR())
      commit('setSearchIdsResult', INIT_SEARCH_IDS_RESULT())
    },
  },

  getters: {
    pendingFilterValue: (state) => (key) => {
      const entry = state.pendingFilters.find((f) => f.key === key)
      return entry ? entry.value : null
    },
    // Matches the legacy sidebar's `count_filtersSelected` (the "Filtri" button's badge number):
    // free-text search is tracked as its own separate state there and never counted.
    countFiltersSelected: (state) => state.pendingFilters.filter((f) => f.key !== 'SmartFilter').length,
    // Matches `has_filtersApplied` (gates the reset button): true when either a real filter is set
    // OR the free-text search is — unlike countFiltersSelected, SmartFilter DOES count here, since
    // "Azzera" must stay enabled while only a search text is active.
    hasPendingFilters: (state, getters) => getters.countFiltersSelected > 0 || state.pendingFilters.some((f) => f.key === 'SmartFilter'),
    issuerName: (state) => (code) => state.issuersByCode[code] || code,
  },
}
